import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { GoogleLoginButton } from '@/components/auth/GoogleLoginButton';
import { PlanComparisonGrid } from '@/components/plans/PlanComparisonGrid';
import { EnterpriseContactForm } from '@/components/plans/EnterpriseContactForm';
import { useTranslation } from '@/hooks/useTranslation';
import { plansApi } from '@/api/plans';
import { useState, useEffect } from 'react';
import type { SubscriptionPlan } from '@/types/plan';
import { Check } from 'lucide-react';

export function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register: registerUser, loginWithGoogle, isLoading, error, clearError } = useAuthStore();
  const { t } = useTranslation();
  
  // Create schema with translations
  const registerSchema = z.object({
    fullName: z.string().min(2, t('validation.minLength', { count: 2 })),
    email: z.string().email(t('validation.email')),
    password: z.string()
      .min(8, t('validation.minLength', { count: 8 }))
      .regex(/[A-Z]/, t('validation.passwordUppercase', 'Password must contain at least one uppercase letter'))
      .regex(/[a-z]/, t('validation.passwordLowercase', 'Password must contain at least one lowercase letter'))
      .regex(/[0-9]/, t('validation.passwordNumber', 'Password must contain at least one number')),
    confirmPassword: z.string(),
    company: z.string().optional(),
    phone: z.string().optional(),
    acceptTerms: z.boolean().refine((val) => val === true, {
      message: 'Você deve aceitar os Termos de Serviço e a Política de Privacidade',
    }),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t('validation.passwordMatch'),
    path: ['confirmPassword'],
  });

  type RegisterFormData = z.infer<typeof registerSchema>;
  
  // Get pre-selected plan from navigation state
  const locationState = location.state as { planCode?: string; showEnterpriseForm?: boolean } | null;
  const preSelectedPlan = locationState?.planCode || 'FREE';
  const showEnterpriseFormInitial = locationState?.showEnterpriseForm || false;
  
  // State management
  const [step, setStep] = useState<'plan' | 'form' | 'enterprise'>('plan');
  const [selectedPlan, setSelectedPlan] = useState<string>(preSelectedPlan);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  
  // Fetch plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const fetchedPlans = await plansApi.getAllPlans();
        setPlans(fetchedPlans);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);
  
  // Handle pre-selected plan after plans are loaded
  useEffect(() => {
    if (!plansLoading && preSelectedPlan && preSelectedPlan !== 'FREE') {
      if (showEnterpriseFormInitial) {
        setStep('enterprise');
      } else {
        setStep('form');
      }
    }
  }, [plansLoading, preSelectedPlan, showEnterpriseFormInitial]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      clearError();
      const { confirmPassword, acceptTerms, ...registerData } = data;
      // Include selected plan in registration
      await registerUser({ ...registerData, planCode: selectedPlan });
      navigate('/dashboard', { replace: true });
    } catch (error) {
      // Error is handled by the store
    }
  };
  
  const handlePlanSelect = (planCode: string) => {
    setSelectedPlan(planCode);
    if (planCode === 'ENTERPRISE') {
      setStep('enterprise');
    } else {
      setStep('form');
    }
  };
  
  const handleEnterpriseSubmit = async (data: any) => {
    try {
      await plansApi.submitEnterpriseContact(data);
      // Show success message and redirect to login
      alert(t('plans.enterpriseContact.successMessage'));
      navigate('/login');
    } catch (error) {
      console.error('Failed to submit enterprise contact:', error);
      alert(t('plans.enterpriseContact.errorMessage'));
    }
  };
  
  const handleBackToPlanSelection = () => {
    setStep('plan');
    setSelectedPlan('FREE');
  };
  
  const getSelectedPlanDetails = () => {
    return plans.find(p => p.planCode === selectedPlan);
  };

  const handleGoogleSuccess = async (token: string) => {
    try {
      clearError();
      await loginWithGoogle({ token });
      navigate('/dashboard', { replace: true });
    } catch (error) {
      // Error is handled by the store
    }
  };

  // Render enterprise contact form
  if (step === 'enterprise') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {t('plans.enterpriseContact.contactTitle')}
            </h2>
            <p className="text-gray-600">
              {plans.find(p => p.planCode === 'ENTERPRISE')?.name || 'Enterprise'} - {plans.find(p => p.planCode === 'ENTERPRISE')?.description || 'For large organizations'}
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-8">
            <EnterpriseContactForm onSubmit={handleEnterpriseSubmit} />
            
            <div className="mt-6 text-center">
              <button
                onClick={handleBackToPlanSelection}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                ← {t('common.back')} {t('plans.selectPlan')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Render plan selection step
  if (step === 'plan') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {t('auth.register')}
            </h2>
            <p className="text-gray-600">
              {t('plans.selectPlan')}
            </p>
            <p className="mt-2 text-sm text-gray-600">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                {t('auth.signInHere')}
              </Link>
            </p>
          </div>

          {plansLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--wl-primary)]"></div>
            </div>
          ) : (
            <PlanComparisonGrid
              plans={plans}
              mode="selection"
              onSelectPlan={handlePlanSelect}
              selectedPlan={selectedPlan}
            />
          )}
        </div>
      </div>
    );
  }

  // Render registration form step
  const selectedPlanDetails = getSelectedPlanDetails();
  
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Registration Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('auth.register')}
                </h2>
                <p className="text-sm text-gray-600">
                  {t('auth.alreadyHaveAccount')}{' '}
                  <Link
                    to="/login"
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    {t('auth.signInHere')}
                  </Link>
                </p>
              </div>

              <div className="space-y-6">
                <GoogleLoginButton
                  onSuccess={handleGoogleSuccess}
                  onError={(error) => console.error('Google login error:', error)}
                />

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">{t('auth.orRegisterWith')}</span>
                  </div>
                </div>

                <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">{error}</h3>
                        </div>
                      </div>
                    </div>
                  )}

                  <Input
                    label={t('auth.fullName')}
                    type="text"
                    autoComplete="name"
                    error={errors.fullName?.message}
                    {...register('fullName')}
                  />

                  <Input
                    label={t('auth.email')}
                    type="email"
                    autoComplete="email"
                    error={errors.email?.message}
                    {...register('email')}
                  />

                  <Input
                    label={t('auth.password')}
                    type="password"
                    autoComplete="new-password"
                    error={errors.password?.message}
                    {...register('password')}
                  />

                  <Input
                    label={t('auth.confirmPassword')}
                    type="password"
                    autoComplete="new-password"
                    error={errors.confirmPassword?.message}
                    {...register('confirmPassword')}
                  />

                  <Input
                    label={`${t('auth.company')} (${t('auth.optional')})`}
                    type="text"
                    autoComplete="organization"
                    error={errors.company?.message}
                    {...register('company')}
                  />

                  <Input
                    label={`${t('auth.phone')} (${t('auth.optional')})`}
                    type="tel"
                    autoComplete="tel"
                    error={errors.phone?.message}
                    {...register('phone')}
                  />

                  <div className="flex items-start">
                    <input
                      id="acceptTerms"
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                      {...register('acceptTerms')}
                    />
                    <label htmlFor="acceptTerms" className="ml-2 block text-sm text-gray-900">
                      {t('auth.termsAgreement')}{' '}
                      <a 
                        href="/termos-de-servico" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-500 underline"
                      >
                        {t('auth.termsOfService')}
                      </a>{' '}
                      {t('auth.and')}{' '}
                      <a 
                        href="/politica-de-privacidade" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-500 underline"
                      >
                        {t('auth.privacyPolicy')}
                      </a>
                    </label>
                  </div>
                  {errors.acceptTerms && (
                    <p className="text-sm text-red-600 -mt-2">
                      {errors.acceptTerms.message}
                    </p>
                  )}

                  <Button type="submit" className="w-full" isLoading={isLoading}>
                    {t('auth.createAccountButton')}
                  </Button>
                </form>
              </div>
            </div>
          </div>

          {/* Plan Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('plans.selectPlan')}
              </h3>
              
              {selectedPlanDetails && (
                <div className="space-y-4">
                  <div className="border-b pb-4">
                    <h4 className="text-xl font-bold text-gray-900">
                      {selectedPlanDetails.name}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedPlanDetails.description}
                    </p>
                  </div>
                  
                  <div className="border-b pb-4">
                    <div className="text-2xl font-bold text-gray-900">
                      {selectedPlanDetails.monthlyPrice === 0 ? (
                        t('plans.pricing.free')
                      ) : (
                        <>
                          R$ {selectedPlanDetails.monthlyPrice.toFixed(2).replace('.', ',')}
                          <span className="text-sm font-normal text-gray-600">
                            {t('plans.pricing.perMonth')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      {t('plans.features.included', 'Features included')}:
                    </p>
                    <ul className="space-y-2">
                      {selectedPlanDetails.maxPublicIntegration !== null && selectedPlanDetails.maxPublicIntegration !== undefined && (
                        <li className="flex items-start gap-2 text-sm text-gray-600">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {selectedPlanDetails.maxPublicIntegration === -1 
                            ? t('plans.usage.unlimited') 
                            : selectedPlanDetails.maxPublicIntegration} {t('plans.usage.publicFeeds')}
                        </li>
                      )}
                      {selectedPlanDetails.dailyEventsLimit !== null && selectedPlanDetails.dailyEventsLimit !== undefined && (
                        <li className="flex items-start gap-2 text-sm text-gray-600">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {selectedPlanDetails.dailyEventsLimit === -1 
                            ? t('plans.usage.unlimited') 
                            : selectedPlanDetails.dailyEventsLimit} {t('plans.usage.privateFeeds')}
                        </li>
                      )}
                      {selectedPlanDetails.monthlyEventsLimit !== null && (
                        <li className="flex items-start gap-2 text-sm text-gray-600">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {selectedPlanDetails.monthlyEventsLimit === -1 
                            ? t('plans.usage.unlimited') 
                            : selectedPlanDetails.monthlyEventsLimit} {t('plans.usage.events')}
                        </li>
                      )}
                      <li className="flex items-start gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        {selectedPlanDetails.supportEnabled ? t('plans.features.prioritySupport', 'Priority support') : t('plans.features.communitySupport', 'Community support')}
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        {selectedPlanDetails.retryEnabled ? t('plans.features.automaticRetry', 'Automatic retry enabled') : t('plans.features.noRetry', 'No automatic retry')}
                      </li>
                    </ul>
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleBackToPlanSelection}
                    className="w-full text-sm text-primary-600 hover:text-primary-500 py-2"
                  >
                    {t('common.back')} {t('plans.selectPlan')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Links de Privacidade e Termos */}
        <div className="text-center mt-4 flex justify-center gap-3 text-xs" style={{ color: 'rgba(50, 71, 92, 0.6)' }}>
          <a
            href="/politica-de-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            Política de Privacidade
          </a>
          <span>•</span>
          <a
            href="/termos-de-servico"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            Termos de Serviço
          </a>
        </div>
      </div>
    </div>
  );
}
